import { ComponentProps, FC, useEffect, useState } from 'react';

import { Loader2, Minus, Plus } from 'lucide-react';

import { InlineOfferEditor } from './InlineOfferEditor';
import { OfferPhotoGallery } from '../../../../components/shared/OfferPhotoGallery';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Checkbox } from '../../../../components/ui/Checkbox';
import { Image } from '../../../../components/ui/Image';
import { Input } from '../../../../components/ui/Input';
import { profileOfferAmountRoute } from '../../../../models/routes';
import { csrfTokenHeader } from '../../../../services/CsrfTokenService';
import { OrdersService } from '../../../../services/OrdersService';
import {
    Offer,
    OfferActionQuantity,
    OfferActionTypes,
    OfferProperty,
    PremiumSummary,
} from '../../../../types';
import { ConfirmModalState } from '../../../../types/ui';
import { formatLocalDateTime } from '../../../../utils';
interface Props {
    offers: Offer[];
    offerProperties: OfferProperty[];
    premiumSummary: PremiumSummary;
    selectedOffers: number[];
    toggleOfferSelection: (offerId: number) => void;
    setConfirmModalState: (state: ConfirmModalState) => void;
    activatingOfferId: number | null;
    premiumErrors: Record<number, string>;
    onPremiumActivationRequest: (offer: Offer) => void;
}

const DEFAULT_PX_OFFSET = 16;
const MIN_OFFER_AMOUNT = 1;
const MAX_OFFER_AMOUNT = 10000;

interface OfferAmountInputProps
    extends Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> {
    value: number;
    onCommitValue: (val: number) => void;
}

const OfferAmountInput: FC<OfferAmountInputProps> = ({
    value,
    onCommitValue,
    onFocus,
    onBlur,
    onKeyDown,
    ...props
}) => {
    const [localValue, setLocalValue] = useState(value.toString());

    useEffect(() => {
        setLocalValue(value.toString());
    }, [value]);

    const commitValue = () => {
        const parsed = Number.parseInt(localValue, 10);

        if (Number.isNaN(parsed)) {
            setLocalValue(value.toString());
            return;
        }

        const normalizedValue = Math.min(
            MAX_OFFER_AMOUNT,
            Math.max(MIN_OFFER_AMOUNT, parsed),
        );

        setLocalValue(normalizedValue.toString());

        if (normalizedValue !== value) {
            onCommitValue(normalizedValue);
        }
    };

    return (
        <Input
            {...props}
            value={localValue}
            onFocus={(event) => {
                setLocalValue('');
                onFocus?.(event);
            }}
            onBlur={(event) => {
                commitValue();
                onBlur?.(event);
            }}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    commitValue();
                    event.currentTarget.blur();
                    return;
                }

                onKeyDown?.(event);
            }}
            onChange={(event) => {
                setLocalValue(event.target.value.replace(/[^\d]/g, ''));
            }}
        />
    );
};

export const OffersList: FC<Props> = ({
    offers,
    offerProperties,
    premiumSummary,
    selectedOffers,
    toggleOfferSelection,
    setConfirmModalState,
    activatingOfferId,
    premiumErrors,
    onPremiumActivationRequest,
}) => {
    const [preview, setPreview] = useState<{
        product: Offer['product'];
        offset: {
            x: number;
            y: number;
        };
    } | null>(null);

    const showProductImage = (
        product: Offer['product'],
        offset: { x: number; y: number },
    ) => {
        setPreview({
            product,
            offset,
        });
    };

    const [offerAmounts, setOfferAmounts] = useState<Record<number, number>>(
        () =>
            Object.fromEntries(
                offers.map((offer) => [offer.id, offer.amount] as const),
            ),
    );
    const [updatingOfferIds, setUpdatingOfferIds] = useState<number[]>([]);
    const [amountErrors, setAmountErrors] = useState<Record<number, string>>(
        {},
    );
    const [editingOfferId, setEditingOfferId] = useState<number | null>(null);

    useEffect(() => {
        setOfferAmounts(
            Object.fromEntries(
                offers.map((offer) => [offer.id, offer.amount] as const),
            ),
        );
        setUpdatingOfferIds([]);
        setAmountErrors({});
        setEditingOfferId((currentOfferId) =>
            offers.some((offer) => offer.id === currentOfferId)
                ? currentOfferId
                : null,
        );
    }, [offers]);

    const updateOfferAmount = async (offer: Offer, nextAmount: number) => {
        if (updatingOfferIds.includes(offer.id)) {
            return;
        }

        const currentAmount = offerAmounts[offer.id] ?? offer.amount;
        const normalizedAmount = Math.min(
            MAX_OFFER_AMOUNT,
            Math.max(MIN_OFFER_AMOUNT, nextAmount),
        );

        if (normalizedAmount === currentAmount) {
            return;
        }

        setOfferAmounts((current) => ({
            ...current,
            [offer.id]: normalizedAmount,
        }));
        setUpdatingOfferIds((current) => [...current, offer.id]);
        setAmountErrors((current) => {
            const nextErrors = { ...current };
            delete nextErrors[offer.id];
            return nextErrors;
        });

        try {
            const response = await fetch(profileOfferAmountRoute(offer.id), {
                method: 'PATCH',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...csrfTokenHeader(),
                },
                body: JSON.stringify({
                    offer: {
                        amount: normalizedAmount,
                    },
                }),
            });
            const payload = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(
                    payload?.errors?.amount ??
                        payload?.errors?.base ??
                        'Не вдалося оновити кількість.',
                );
            }

            setOfferAmounts((current) => ({
                ...current,
                [offer.id]: payload.amount,
            }));
        } catch (error) {
            setOfferAmounts((current) => ({
                ...current,
                [offer.id]: currentAmount,
            }));
            setAmountErrors((current) => ({
                ...current,
                [offer.id]:
                    error instanceof Error
                        ? error.message
                        : 'Не вдалося оновити кількість.',
            }));
        } finally {
            setUpdatingOfferIds((current) =>
                current.filter((offerId) => offerId !== offer.id),
            );
        }
    };

    return (
        <>
            {preview && (
                <img
                    data-testid="product-image-preview"
                    className={'fixed z-100 h-[350px] w-[250px] rounded-2xl'}
                    style={{
                        top: `${preview.offset.y + DEFAULT_PX_OFFSET}px`,
                        left: `${preview.offset.x + DEFAULT_PX_OFFSET}px`,
                    }}
                    src={preview.product.image}
                    alt={preview.product.image_alt}
                />
            )}
            <div className="mb-6 space-y-3">
                {offers.map((offer) => {
                    const offerPropertyEntries =
                        OrdersService.getOfferPropertyEntries(
                            offer.property_set,
                            offerProperties,
                        );
                    const offerPropertiesLabel = offerPropertyEntries
                        .map((entry) => `${entry.key}: ${entry.value}`)
                        .join(', ');
                    const currentAmount =
                        offerAmounts[offer.id] ?? offer.amount;
                    const isUpdatingAmount = updatingOfferIds.includes(
                        offer.id,
                    );
                    const isEditing = editingOfferId === offer.id;
                    const amountControl = (
                        <div
                            className="flex items-center gap-1"
                            data-testid={`profile-offer-list-item-amount-control-${offer.id}`}
                            aria-busy={isUpdatingAmount}
                        >
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-6 w-6 bg-transparent"
                                onClick={() =>
                                    updateOfferAmount(offer, currentAmount - 1)
                                }
                                disabled={
                                    isUpdatingAmount ||
                                    currentAmount <= MIN_OFFER_AMOUNT
                                }
                                data-testid={`profile-offer-list-item-amount-decrement-${offer.id}`}
                                aria-label="Зменшити кількість пропозиції"
                            >
                                {isUpdatingAmount ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <Minus className="h-3 w-3" />
                                )}
                            </Button>
                            <OfferAmountInput
                                type="number"
                                min={MIN_OFFER_AMOUNT}
                                max={MAX_OFFER_AMOUNT}
                                value={currentAmount}
                                disabled={isUpdatingAmount}
                                onCommitValue={(value) =>
                                    updateOfferAmount(offer, value)
                                }
                                className="h-6 w-12 px-1 text-center text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                data-testid={`profile-offer-list-item-amount-input-${offer.id}`}
                                aria-label="Кількість пропозиції"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-6 w-6 bg-transparent"
                                onClick={() =>
                                    updateOfferAmount(offer, currentAmount + 1)
                                }
                                disabled={
                                    isUpdatingAmount ||
                                    currentAmount >= MAX_OFFER_AMOUNT
                                }
                                data-testid={`profile-offer-list-item-amount-increment-${offer.id}`}
                                aria-label="Збільшити кількість пропозиції"
                            >
                                {isUpdatingAmount ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                    <Plus className="h-3 w-3" />
                                )}
                            </Button>
                        </div>
                    );

                    return (
                        <div
                            key={offer.id}
                            className="group overflow-hidden rounded-xl border"
                        >
                            <div className="flex flex-wrap items-start gap-3 p-3 hover:bg-muted/50 sm:flex-nowrap sm:items-center">
                                <Checkbox
                                    checked={selectedOffers.includes(offer.id)}
                                    onCheckedChange={() =>
                                        toggleOfferSelection(offer.id)
                                    }
                                    className="h-4 w-4"
                                />
                                <div className="relative h-16 w-12 flex-shrink-0 overflow-hidden rounded bg-muted">
                                    <Image
                                        src={offer.product.image}
                                        alt={offer.product.image_alt}
                                        className="object-cover"
                                        data-testid="profile-offer-list-item-image"
                                        onTouchStart={(event) => {
                                            event.stopPropagation();
                                            showProductImage(offer.product, {
                                                x: event.touches[0].clientX,
                                                y: event.touches[0].clientY,
                                            });
                                        }}
                                        onMouseEnter={(event) =>
                                            showProductImage(offer.product, {
                                                x: event.clientX,
                                                y: event.clientY,
                                            })
                                        }
                                        onMouseMove={(event) =>
                                            showProductImage(offer.product, {
                                                x: event.clientX,
                                                y: event.clientY,
                                            })
                                        }
                                        onMouseOut={() => {
                                            setPreview(null);
                                        }}
                                    />
                                </div>
                                <OfferPhotoGallery
                                    photos={offer.photos}
                                    compact
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3
                                            className="w-full text-sm font-medium sm:w-auto"
                                            data-testid="profile-offer-list-item-name"
                                        >
                                            {offer.product.name}
                                        </h3>
                                        <Badge
                                            variant={
                                                offer.status === 'active'
                                                    ? 'success'
                                                    : offer.status ===
                                                        'inactive'
                                                      ? 'secondary'
                                                      : 'default'
                                            }
                                            className="px-1.5 text-xs"
                                        >
                                            {offer.status === 'active'
                                                ? 'Активна'
                                                : offer.status === 'inactive'
                                                  ? 'Призупинена'
                                                  : offer.status
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                    offer.status.slice(1)}
                                        </Badge>
                                        <Badge className="mr-1 text-xs">
                                            {offer.product.language}
                                        </Badge>
                                        <Badge
                                            variant="outline"
                                            className="text-xs"
                                        >
                                            {offer.product.expansion_tag}
                                        </Badge>
                                        {offer.premium_active && (
                                            <Badge
                                                variant="warning"
                                                className="text-xs"
                                            >
                                                Premium до{' '}
                                                {formatLocalDateTime(
                                                    offer.premium_until || '',
                                                )}
                                            </Badge>
                                        )}
                                    </div>
                                    <div
                                        className="mt-1 flex min-w-0 flex-wrap items-start gap-x-4 gap-y-1 text-xs text-muted-foreground"
                                        data-testid="profile-offer-list-item-details"
                                    >
                                        <div
                                            data-testid={`profile-offer-list-item-views-${offer.id}`}
                                        >
                                            Переглядів: {offer.views_count}
                                        </div>
                                        {offerPropertiesLabel && (
                                            <div className="w-full min-w-0 [overflow-wrap:anywhere] break-words">
                                                {offerPropertiesLabel}
                                            </div>
                                        )}
                                    </div>
                                    {amountErrors[offer.id] && (
                                        <p className="mt-1 text-xs text-red-600">
                                            {amountErrors[offer.id]}
                                        </p>
                                    )}
                                    {premiumErrors[offer.id] && (
                                        <p className="mt-1 text-xs text-red-600">
                                            {premiumErrors[offer.id]}
                                        </p>
                                    )}
                                </div>
                                <div className="mt-2 w-full text-left sm:mt-0 sm:w-auto sm:flex-shrink-0 sm:text-right">
                                    <div
                                        className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end"
                                        data-testid={`profile-offer-list-item-price-block-${offer.id}`}
                                    >
                                        {amountControl}
                                        <div
                                            className="font-semibold"
                                            data-testid={`profile-offer-list-item-price-${offer.id}`}
                                        >
                                            ₴{offer.price.toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex w-full gap-2 sm:w-40 sm:flex-shrink-0 sm:flex-col">
                                    {premiumSummary.availableSlotsCount > 0 && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="secondary"
                                            className="h-8 min-w-0 flex-1 border border-amber-400 bg-white px-2 text-xs font-semibold text-amber-700 shadow-sm hover:bg-amber-50 sm:w-full sm:flex-none"
                                            onClick={() =>
                                                onPremiumActivationRequest(
                                                    offer,
                                                )
                                            }
                                            disabled={
                                                activatingOfferId === offer.id
                                            }
                                            data-testid={`profile-offer-premium-button-${offer.id}`}
                                        >
                                            {activatingOfferId === offer.id ? (
                                                'Активуємо...'
                                            ) : offer.premium_active ? (
                                                <>
                                                    <span className="sm:hidden">
                                                        Premium
                                                    </span>
                                                    <span className="hidden sm:inline">
                                                        Продовжити premium
                                                    </span>
                                                </>
                                            ) : (
                                                'Premium 7 днів'
                                            )}
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={
                                            isEditing
                                                ? 'outlinePrimary'
                                                : 'outline'
                                        }
                                        className="h-8 min-w-0 flex-1 sm:w-full sm:flex-none"
                                        onClick={() =>
                                            setEditingOfferId(
                                                isEditing ? null : offer.id,
                                            )
                                        }
                                        aria-expanded={isEditing}
                                    >
                                        Редагувати
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setConfirmModalState({
                                                open: true,
                                                payload: {
                                                    type: OfferActionTypes.Delete,
                                                    quantity:
                                                        OfferActionQuantity.Single,
                                                    offerId: offer.id,
                                                },
                                            });
                                        }}
                                        size="sm"
                                        variant="destructive"
                                        className="h-8 min-w-0 flex-1 sm:w-full sm:flex-none"
                                        asChild
                                    >
                                        <span>Видалити</span>
                                    </Button>
                                </div>
                            </div>
                            {isEditing && (
                                <div className="border-t bg-muted/20 p-3 sm:p-4">
                                    <InlineOfferEditor
                                        offer={offer}
                                        offerProperties={offerProperties}
                                        onCancel={() => setEditingOfferId(null)}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};
